import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { SimpleAreaChart, RangeSelector } from "@/components/Charts";
import { fmtMetric, fmtPct, fmtCompact, gainClass, fmtDate } from "@/lib/format";
import { ArrowLeft } from "lucide-react";

export default function MetricDetail() {
    const { key } = useParams();
    const navigate = useNavigate();
    const [range, setRange] = useState("YTD");

    const { data, isLoading } = useQuery({ queryKey: ["metric", key, range], queryFn: async () => (await api.get(`/market/series?key=${key}&range=${range}`)).data });

    const fmt = data?.fmt || "num";
    const quote = data?.quote;
    const series = data?.series || [];

    const axisFmt = (v) => {
        if (fmt === "pct") return `${Number(v).toFixed(1)}%`;
        if (fmt === "usd") return fmtCompact(v);
        if (fmt === "fx") return `₪${Number(v).toFixed(2)}`;
        return Number(v).toLocaleString("en-US", { maximumFractionDigits: 0 });
    };
    const tipFmt = (v) => fmtMetric(fmt, v);

    return (
        <div className="space-y-6">
            <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="-ml-2" data-testid="metric-back-btn"><ArrowLeft className="w-4 h-4 mr-1.5" />Back</Button>

            {isLoading ? <Skeleton className="h-28 rounded-xl" /> : (
                <div className="flex flex-wrap items-end justify-between gap-4">
                    <div>
                        <div className="flex items-center gap-3">
                            <h1 className="font-heading text-3xl font-bold tracking-tight">{data?.label}</h1>
                            <Badge variant="secondary" className="num">{data?.symbol}</Badge>
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">{quote?.source}</p>
                    </div>
                    <div className="text-right">
                        <div className="num text-3xl font-bold">{fmtMetric(fmt, quote?.price)}</div>
                        {quote && (
                            <div className={`num text-sm ${gainClass(quote.change)}`}>
                                {quote.change > 0 ? "+" : ""}{fmtMetric(fmt, quote.change).replace("₪", "").replace("$", "")} ({fmtPct(quote.change_percent)})
                            </div>
                        )}
                    </div>
                </div>
            )}

            <Card className="p-5">
                <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                    <h2 className="font-heading text-lg font-semibold">Price History</h2>
                    <RangeSelector value={range} onChange={setRange} testPrefix="metric-range" />
                </div>
                {isLoading ? <Skeleton className="h-[380px] rounded-lg" /> : (
                    <SimpleAreaChart data={series} axisFmt={axisFmt} tipFmt={tipFmt}
                        color={series.length && series[series.length - 1].value >= series[0].value ? "hsl(var(--gain))" : "hsl(var(--loss))"} />
                )}
                {series.length > 0 && (
                    <p className="text-xs text-muted-foreground mt-2 num">
                        {fmtDate(series[0].date)} → {fmtDate(series[series.length - 1].date)} · {series.length} points
                    </p>
                )}
            </Card>
        </div>
    );
}
